import moment from "moment";

/**
 * The clinic's clock.
 *
 * Some of what the intake API hands out is a plain wall-clock reading with no offset on it — the
 * slots on the calendar and `booking_time` on a note, both written on the clinic's clock. (Not
 * everything: `hold_booking_until` is serialized with its offset, so it reads back correctly on
 * its own.) Reading a bare one back with `new Date()` or a bare `moment()` silently
 * re-dates it onto whatever clock the patient's device keeps, so a patient sitting a few hours
 * ahead of the clinic sees the slot they have just booked as already gone, and the calendar offers
 * them the very slot it says they cannot have.
 *
 * `/api/patient-intake` says which timezone the clinic keeps, so the honest comparison is to put
 * `now` on that same wall clock and compare the two readings. Both are then parsed in the device's
 * frame, so whatever its offset happens to be, it cancels.
 */

/** The shape every booking time arrives in: `2026-09-22 10:20:00`. */
export const PATIENT_INTAKE_CLOCK_FORMAT = "YYYY-MM-DD HH:mm:ss";

/**
 * Formatters by timezone.
 *
 * Constructing one resolves the runtime's timezone data, which is among the more expensive things
 * Intl does — and the only caller asks on every render of a page a patient reaches on a phone.
 * There is one clinic per session, so the map never grows past a handful of entries.
 */
const clinicClockFormatters = new Map();

const getClinicClockFormatter = (timezone) => {
	if (!clinicClockFormatters.has(timezone)) {
		clinicClockFormatters.set(
			timezone,
			new Intl.DateTimeFormat("en-GB", {
				timeZone: timezone,
				hourCycle: "h23",
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			})
		);
	}

	return clinicClockFormatters.get(timezone);
};

/** `now` as the clinic would write it down, or the device's own reading when it cannot be told. */
export const getPatientIntakeClinicWallClock = (timezone = "", now = new Date()) => {
	if (!timezone) return moment(now).format(PATIENT_INTAKE_CLOCK_FORMAT);

	try {
		const parts = Object.fromEntries(
			getClinicClockFormatter(timezone)
				.formatToParts(now)
				.map(({ type, value }) => [type, value])
		);

		// A runtime that writes midnight as hour 24 rather than 00 would produce a reading no
		// format string parses, and a booking at midnight would then be judged against the wrong
		// day. One line is cheaper than finding out which runtimes do it.
		const hour = parts.hour === "24" ? "00" : parts.hour;

		return `${parts.year}-${parts.month}-${parts.day} ${hour}:${parts.minute}:${parts.second}`;
	} catch (_error) {
		// A name the runtime will not accept must not be cached as a broken formatter.
		clinicClockFormatters.delete(timezone);

		// An unknown timezone name is not worth breaking the page over — the server checks the
		// booking again before it takes the money, and it is the one that decides.
		return moment(now).format(PATIENT_INTAKE_CLOCK_FORMAT);
	}
};

/**
 * Whether the appointment a booking holds has already been and gone, on the clinic's clock.
 *
 * A booking with no time on it has nothing to lose, so it never counts as passed.
 */
export const isPatientIntakeBookingTimePassed = ({ bookingTime = "", timezone = "" } = {}, now = new Date()) => {
	const booking = moment(String(bookingTime || "").trim(), PATIENT_INTAKE_CLOCK_FORMAT);
	if (!bookingTime || !booking.isValid()) return false;

	return booking.isBefore(moment(getPatientIntakeClinicWallClock(timezone, now), PATIENT_INTAKE_CLOCK_FORMAT));
};

/**
 * The same question asked of what the intake screens actually hold: a note, and the payload
 * `/api/patient-intake` answered with.
 *
 * Named here rather than spelled out at the call site so the two keys it depends on —
 * `note.booking_time` and `patientIntakeData.timezone` — are pinned by a test. Getting the second
 * one wrong costs nothing visible: the helper falls back to the device's own clock, which is the
 * bug this exists to remove.
 */
export const isPatientIntakeNoteBookingPassed = (note, patientIntakeData, now = new Date()) =>
	isPatientIntakeBookingTimePassed({ bookingTime: note?.booking_time, timezone: patientIntakeData?.timezone }, now);
